/**
 * Registry of SSR requests that have been started but have not come back yet,
 * keyed by the query boundary (data fetcher name) they were started under.
 *
 * A data fetcher times out *because* one of its requests has not returned, so the
 * culprit can only be named while it is still in flight. Logging when a response
 * arrives misses the request that never responds at all — which is the common case.
 */

export type InflightRequest = {
	method: string;
	url: string;
	/** `Date.now()` at the time the request was started. */
	startTime: number;
};

declare global {
	interface Window {
		__SSR_INFLIGHT_REQUESTS__?: Map<string, Set<InflightRequest>>;
	}
}

const noop = () => {};

/**
 * Marks a request as in flight for `queryBoundary`. Returns a function that MUST be
 * called once the request settles — resolved, rejected or aborted — to unregister it.
 *
 * SSR-only: a no-op in the browser, or when there is no query boundary to attribute
 * the request to. Requests fired outside a boundary are deliberately NOT tracked: SSR
 * blocks them with a never-settling promise (`SSRLink`, `stopFetchCalls`), so tracking
 * them would leave permanent entries in the registry and report requests that were
 * never actually sent as the cause of a timeout.
 */
export function trackInflightRequest(
	queryBoundary: string | undefined | null,
	request: { method: string; url: string },
): () => void {
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (!process.env.REACT_SSR || !queryBoundary) {
		return noop;
	}

	if (!window.__SSR_INFLIGHT_REQUESTS__) {
		window.__SSR_INFLIGHT_REQUESTS__ = new Map();
	}
	const registry = window.__SSR_INFLIGHT_REQUESTS__;

	const existing = registry.get(queryBoundary);
	const requests = existing ?? new Set<InflightRequest>();
	if (!existing) {
		registry.set(queryBoundary, requests);
	}

	const inflightRequest: InflightRequest = { ...request, startTime: Date.now() };
	requests.add(inflightRequest);

	return () => {
		requests.delete(inflightRequest);
		// Only drop the boundary if this is still its current set — a second call of this
		// untrack function must not discard requests registered in the meantime.
		if (requests.size === 0 && registry.get(queryBoundary) === requests) {
			registry.delete(queryBoundary);
		}
	};
}

/**
 * Returns the requests started under `queryBoundary` that have not settled yet.
 *
 * SSR-only: always empty in the browser.
 */
export function getInflightRequests(queryBoundary: string | undefined | null): InflightRequest[] {
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (!process.env.REACT_SSR || !queryBoundary) {
		return [];
	}

	const requests = window.__SSR_INFLIGHT_REQUESTS__?.get(queryBoundary);
	return requests ? Array.from(requests) : [];
}
